import { api, downloadFile } from './client';
import type {
  AnalysisStatusResponse,
  ApplyJobResponse,
  ApplyOutcome,
  ApplyProgress,
  AttributesResponse,
  AuthResponse,
  SupportGrant,
  BriefingResponse,
  CatalogContext,
  CatalogContextInput,
  CatalogProfileResponse,
  CatalogResponse,
  CellEditRequest,
  CellEditResponse,
  CreateInvitationResponse,
  DataSource,
  Destination,
  DestinationDetail,
  MembershipChange,
  MembershipSelection,
  Invitation,
  Role,
  TeamMember,
  TeamMembersResponse,
  FacetFieldsResponse,
  FacetRequest,
  FacetResponse,
  FindingCountsResponse,
  FindingFieldsResponse,
  FindingResponse,
  HistoryAction,
  PaginatedHistoryResponse,
  Scope,
  FindingStatus,
  MeResponse,
  PaginatedFindingsResponse,
  PaginatedResponse,
  EntityResponse,
  ExportFormat,
  TablePage,
  TableQuery,
} from '../types';

/**
 * Result of an apply POST (SC-2580). `applied` = a synchronous apply — a
 * per-entity Fix (`200` + an {@link ApplyOutcome} ledger in `outcome`, SC-2908)
 * or a whole finding the BE ran inline (`204`, no body → `outcome: null`);
 * `queued` = a `202` with a job to poll via `findingsApi.applyProgress`.
 */
export type ApplyResult =
  | { kind: 'applied'; outcome: ApplyOutcome | null }
  | { kind: 'queued'; job: ApplyJobResponse };

/**
 * A rendered table export (SC-3149), with the metadata its response headers
 * carry. The counts are what the server actually WROTE, which is what the UI
 * reports — not the count the grid showed when the user clicked. `null` when a
 * header is absent or unparseable (an older backend, or a proxy stripping it):
 * unknown, not zero.
 */
export interface TableExport {
  blob: Blob;
  /** Raw `Content-Disposition` — unsanitised; see `exportFilename`. */
  contentDisposition: string | null;
  rowCount: number | null;
  columnCount: number | null;
  /** Catalog columns the file could not carry. `0` when the header is absent —
   *  the backend sets it ONLY when something was dropped. */
  droppedCount: number;
  /** The dropped names, possibly truncated server-side (the header is
   *  byte-bounded); `droppedCount` is always the exact number. */
  droppedColumns: string[];
}

function countHeader(headers: Headers, name: string): number | null {
  const raw = headers.get(name);
  if (raw === null || !/^\d+$/.test(raw.trim())) return null;
  return Number(raw.trim());
}

function droppedColumnsHeader(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch (e) {
        if (e instanceof URIError) return part;
        throw e;
      }
    });
}

export interface LoginBody {
  email: string;
  password: string;
}

export interface RegisterBody {
  email: string;
  password: string;
  name: string;
  org_name: string;
}

// Note: no `refresh` here on purpose. /auth/refresh is called only by the
// internal refreshAccessToken() in client.ts via raw fetch, so it bypasses
// the request() wrapper. Routing it through `api.post` would recurse into
// the silent-refresh handler on 401 and double-call the endpoint.
export const authApi = {
  login: (body: LoginBody) => api.post<AuthResponse>('/auth/login', body),
  register: (body: RegisterBody) => api.post<AuthResponse>('/auth/register', body),
  me: () => api.get<MeResponse>('/auth/me'),
  logout: (refresh_token: string) =>
    api.post<void>('/auth/logout', { refresh_token }),
  // Re-scope the session to an org the caller ALREADY holds a live membership
  // in, or back to the platform scope with `null` (SC-2668). Mints a fresh
  // token pair; never creates access — 403 when no live membership matches
  // (e.g. a support grant that has expired or been revoked).
  switchOrg: (organizationId: string | null) =>
    api.post<AuthResponse>('/auth/switch-org', { organization_id: organizationId }),
};

// Staff support grants (SC-2668 BE / SC-2948 FE), EKOM_ADMIN-gated. The list is
// every staff member's LIVE grants across all orgs (not just the caller's),
// newest grant first, paginated with a server cap of 100 per page.
export const supportAccessApi = {
  listLive: (page = 1, limit = 100) =>
    api.get<PaginatedResponse<SupportGrant>>(
      `/admin/support-access?page=${page}&limit=${limit}`,
    ),
};

export interface InviteBody {
  email: string;
  role: Role;
}

// The invitee's accept payload (SC-2791). The single-use `token` from the magic
// link IS the credential — the endpoint is public/unauthenticated. `name` +
// `password` are sent when creating a brand-new account; the BE ignores them
// when the email already has a user (membership-attach, the multi-org case) and
// rejects a new account that omits them (422). SSO invitees never hit this path
// — they're honored by verified email through the normal SSO flow (SC-2796).
export interface AcceptInviteBody {
  token: string;
  name?: string;
  password?: string;
}

// Tenant-facing team invitations (SC-2795) — the org owner's own-org invite
// surface, gated on INVITE_MEMBER. Org is implicit in the session (single-org
// scoped), so nothing here takes an org id. `list` returns PENDING invites only,
// paginated (`page`/`limit`, newest first). `revoke` is a POST to `/revoke`
// (not a DELETE). `accept` is the public recipient side (SC-2791) — it returns a
// full session (AuthResponse), same shape as login. The staff-facing
// provisioning console (SC-2784) is separate.
export const invitationsApi = {
  create: (body: InviteBody) =>
    api.post<CreateInvitationResponse>('/team/invitations', body),
  list: (page = 1, limit = 20) =>
    api.get<PaginatedResponse<Invitation>>(
      `/team/invitations?page=${page}&limit=${limit}`,
    ),
  revoke: (invitationId: string) =>
    api.post<Invitation>(`/team/invitations/${invitationId}/revoke`),
  accept: (body: AcceptInviteBody) =>
    api.post<AuthResponse>('/team/invitations/accept', body),
};

// The org's roster (SC-2882 BE / SC-3227 FE). The read is any-active-member and
// deliberately unpaginated (a bare `{members}` envelope — no limit, no cursor);
// the title write is gated on EDIT_MEMBER_TITLE.
export const teamMembersApi = {
  list: () => api.get<TeamMembersResponse>('/team/members'),
  // `title: null` (or blank) clears it.
  setTitle: (membershipId: string, title: string | null) =>
    api.patch<TeamMember>(`/team/members/${membershipId}/title`, { title }),
};

export const catalogsApi = {
  list: () => api.get<CatalogResponse[]>('/catalogs'),
  get: (id: string) => api.get<CatalogResponse>(`/catalogs/${id}`),
  // `destinationSlugs` stamps channel membership on every row as the catalog is
  // built (SC-3104/SC-3148) — "this whole file is Shopify". Repeated form fields,
  // the same shape `locked_fields` uses.
  //
  // Omitted and `[]` are identical on the wire (the loop appends nothing), and
  // both mean membership of NOTHING — the safe direction, and the default.
  //
  // An UNKNOWN slug fails the WHOLE upload with a 422 before a single row lands:
  // resolution is deliberately all-or-nothing server-side, because a partially
  // resolved set ("Shopify and Amazon", where `amazon` is a typo) would tag half
  // the catalog and nobody would notice until the export came back short. The
  // caller validates against a fresh `/destinations` list first so that round
  // trip is rare — see `CatalogUpload`.
  //
  // No membership permission gates this. The backend runs it under CREATE_CATALOG
  // on purpose: it widens what uploading already does rather than adding a
  // capability, so `manage_destination_membership` (which guards every OTHER
  // membership write) is deliberately absent here.
  upload: (file: File, lockedFields?: string[], destinationSlugs?: string[]) => {
    const fd = new FormData();
    fd.append('file', file);
    lockedFields?.forEach((f) => fd.append('locked_fields', f));
    destinationSlugs?.forEach((s) => fd.append('destination_slugs', s));
    return api.upload<CatalogResponse>('/catalogs/upload', fd);
  },
  // Method name uses the FE-internal `entities` term (SC-2196); the REST path
  // stays `/products` per the SC-2191 ticket — the BE keeps that route until
  // a future rename.
  entities: (id: string, page = 1, perPage = 25) =>
    api.get<PaginatedResponse<EntityResponse>>(
      `/catalogs/${id}/products?page=${page}&per_page=${perPage}`
    ),
  // SC-2376 unified table-query endpoint (POST — the query is a structured body).
  // Resolves a `TableQuery` (filters + columns + cursor + limit) into one
  // projected page of rows plus the findings overlay for those rows. Supersedes
  // the `entities` (`/products`) list for the grid; that legacy route stays until
  // the FE is fully off it (SC-2372 cutover), then the BE deletes it.
  table: (id: string, query: TableQuery) =>
    api.post<TablePage>(`/catalogs/${id}/table`, query),
  // SC-3149 — the grid's slice as a file. Posts the SAME `TableQuery` the grid
  // holds, unchanged, so the exported rows are by construction the rows on
  // screen (the backend ignores `cursor`/`limit` — an export is the whole
  // slice). Columns are the catalog's identity columns first, then `columns`
  // in the order sent — `column_order=request` (SC-3232/SC-3244), because the
  // backend's default is the catalog's upload order and the panel's list IS
  // the file's order. Refused with a 422 over the synchronous row budget
  // (25,000 at ordinary width). Ungated: any member.
  export: async (id: string, query: TableQuery, format: ExportFormat): Promise<TableExport> => {
    const { blob, headers } = await downloadFile(
      `/catalogs/${id}/table/export?format=${format}&column_order=request`,
      query,
    );
    const droppedCount = countHeader(headers, 'X-Export-Dropped-Count') ?? 0;
    return {
      blob,
      contentDisposition: headers.get('Content-Disposition'),
      rowCount: countHeader(headers, 'X-Export-Row-Count'),
      columnCount: countHeader(headers, 'X-Export-Column-Count'),
      droppedCount,
      droppedColumns:
        droppedCount > 0 ? droppedColumnsHeader(headers.get('X-Export-Dropped-Columns')) : [],
    };
  },
  // SC-2376 Slice E — the facet rail's counts (SC-2395). Takes a thin wrapper
  // over the SAME `TableQuery` (only `filters` drive the counts); returns
  // exclude-own-filter `FacetGroup[]`. Fired on filter-change, NOT page-flip —
  // counts are a function of the filter set, not the cursor.
  facets: (id: string, request: FacetRequest) =>
    api.post<FacetResponse>(`/catalogs/${id}/table/facets`, request),
  // SC-3466 — the rail's layout without counts: pinned groups + every faceable
  // group (the rail's one list). One profile read, no aggregations.
  facetFields: (id: string) => api.get<FacetFieldsResponse>(`/catalogs/${id}/table/facet-fields`),
  // SC-3466 — replace the catalog's pinned groups (admin). `null` resets to the
  // default; `[]` pins nothing. Echoes the layout.
  setPinnedFilters: (id: string, fields: string[] | null) =>
    api.put<FacetFieldsResponse>(`/catalogs/${id}/table/pinned-filters`, { fields }),
  // SC-2376 Slice G — single-entity full-document read (the "data axis": one
  // entity × ALL values, no projection). Powers the SC-2378 drawer's "Show all
  // attributes" once the grid moves to projected `/table` rows. `entityId` is
  // the catalog identity value (the SKU that `/table` rows carry as entity_id);
  // encode it since identifiers can contain path-unsafe chars. Null-identifier
  // catalogs can't deep-fetch.
  entityDetail: (id: string, entityId: string) =>
    api.get<EntityResponse>(
      `/catalogs/${id}/entities/${encodeURIComponent(entityId)}`,
    ),
  // SC-3517 — the same full document, addressed by the row's Mongo `_id` rather
  // than the SKU (which can repeat, or be absent). 404 = the row is gone (a
  // re-upload replaced it) or the id is unknown/malformed.
  rowDetail: (id: string, rowId: string) =>
    api.get<EntityResponse>(`/catalogs/${id}/rows/${encodeURIComponent(rowId)}`),
  // BE rejects empty/whitespace-only names and anything over 100 chars with 422;
  // the caller (ActiveCatalogCard) already trims and guards empty pre-mutate.
  rename: (id: string, name: string) =>
    api.patch<CatalogResponse>(`/catalogs/${id}`, { name }),
  // SC-3106 — a person fixing one cell. Addressed by the row's `_id` in the BODY
  // (never the identifier, which can be null or repeat; and the field goes in the
  // body because column names carry slashes and spaces). Strictly grounded on
  // `before`: a resend of an edit that already landed is a 409, not a second
  // history entry, so a caller reads every 409 against a refetch of the cell.
  // Gated `edit_entity_value` (every member).
  cellEdit: (id: string, body: CellEditRequest) =>
    api.post<CellEditResponse>(`/catalogs/${id}/cell-edits`, body),
};

// SC-3104 — the org's sync destinations (the FE calls them CHANNELS in user-facing
// copy). SC-3121 added the write API (create / get / PATCH / DELETE, all behind
// `manage_destinations`); the FE uses only the detail read and the kill switch so
// far (SC-3122). Create, delete and the boundary-policy fields are tier-2 work.
//
// A bare array, not a paginated envelope — deliberately unpaginated, like
// `catalogsApi.list()`. There's no `total`/`next` to read and no truncation to
// surface; an org has a handful of destinations, not a catalog's worth.
export const destinationsApi = {
  // Ungated — any active member. Returns enabled AND disabled destinations; the
  // caller must render a disabled one as paused rather than dropping it, or an
  // entity's membership in a paused channel renders as an unresolvable id.
  list: () => api.get<Destination[]>('/destinations'),

  // One destination in full, incl. `catalog_ids` and `lifecycle` — the list omits
  // both. `manage_destinations` (ORG_ADMIN); another org's id is a 404, not a 403.
  get: (id: string) => api.get<DestinationDetail>(`/destinations/${encodeURIComponent(id)}`),

  // The kill switch. Deliberately NOT a general `update(body)`: PATCH reads which
  // keys were SENT, and on `catalog_ids` / `allowed_fields` a `null` means
  // "everything" — so a helper that forwarded a partial object could widen what
  // leaves the building by carrying one stray key. This can only ever send
  // `enabled`. 422 once the destination is `deleting`.
  setEnabled: (id: string, enabled: boolean) =>
    api.patch<DestinationDetail>(`/destinations/${encodeURIComponent(id)}`, { enabled }),

  // SC-3147 — add entities to a channel's assortment. POST rather than PUT: the
  // gesture ADDS to a set (`$addToSet` server-side), so re-running it is a no-op
  // rather than a duplicate, and nothing here can clear a membership by omission.
  //
  // Requires `manage_destination_membership` (ORG_ADMIN floor); 403 below that.
  addMembers: (catalogId: string, slug: string, selection: MembershipSelection) =>
    api.post<MembershipChange>(
      `/catalogs/${catalogId}/destinations/${encodeURIComponent(slug)}/members`,
      selection,
    ),

  // Removal is its own POST, NOT a DELETE, and that is the backend's deliberate
  // choice rather than an oversight: the selection is a structured body, and a
  // body on DELETE is stripped by enough clients and proxies that "remove these 40
  // rows" could arrive as "remove nothing" — or, under a laxer contract, as
  // "remove everything".
  removeMembers: (catalogId: string, slug: string, selection: MembershipSelection) =>
    api.post<MembershipChange>(
      `/catalogs/${catalogId}/destinations/${encodeURIComponent(slug)}/members/remove`,
      selection,
    ),
};

// The org's inbound sources (app/sources) — READ-ONLY over the API; mutations are
// script-only (`make manage-sources`). The whole router sits behind `view_sources`
// (ORG_ADMIN), so a caller below that gets a 403 — gate the query, don't swallow
// it. A bare array, heaviest weight first; an org has a handful.
export const sourcesApi = {
  list: () => api.get<DataSource[]>('/sources'),
};

export type FindingsSortColumn = 'severity' | 'affected' | 'field';
export type FindingsSortDir = 'asc' | 'desc';

export interface ListFindingsParams {
  status?: FindingStatus[];
  // Filter by finding breadth (SC-2524). `field`/`catalog` for the Attributes
  // surface; omit for all scopes. Repeated `scope=` params, OR-ed server-side.
  scope?: Scope[];
  // SC-3532 / SC-3533 — every Findings-view axis is a SERVER param, so `total` and
  // each page are the same filtered set. `field` + `kind` are repeated params, OR-ed
  // within the axis; `minSeverity` is a band floor.
  field?: string[];
  kind?: ('fix' | 'review')[];
  minSeverity?: 'low' | 'medium' | 'high' | 'critical';
  sort?: FindingsSortColumn;
  dir?: FindingsSortDir;
  page?: number;
  perPage?: number;
}

/** The list's query string — shared by `list` and the field-options call, which
 *  takes the same filters (it ignores `field`, paging and sort itself). */
function findingsQueryString(params: ListFindingsParams): string {
  const qs = new URLSearchParams();
  params.status?.forEach((s) => qs.append('status', s));
  params.scope?.forEach((s) => qs.append('scope', s));
  params.field?.forEach((f) => qs.append('field', f));
  params.kind?.forEach((k) => qs.append('kind', k));
  if (params.minSeverity) qs.set('min_severity', params.minSeverity);
  if (params.sort) qs.set('sort', params.sort);
  if (params.dir) qs.set('dir', params.dir);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.perPage !== undefined) qs.set('per_page', String(params.perPage));
  const query = qs.toString();
  return query ? `?${query}` : '';
}

export const findingsApi = {
  list: (catalogId: string, params: ListFindingsParams = {}) =>
    api.get<PaginatedFindingsResponse>(
      `/normalization/catalogs/${catalogId}/findings${findingsQueryString(params)}`,
    ),
  // One finding by id — the overlay's fallback when `?finding=<id>` isn't on the
  // loaded page (SC-3532: the list is paged, so a shared link can point past it).
  get: (catalogId: string, findingId: string) =>
    api.get<FindingResponse>(`/normalization/catalogs/${catalogId}/findings/${findingId}`),
  // The field filter's options (SC-3533): fields with findings under the other filters.
  fields: (catalogId: string, params: Omit<ListFindingsParams, 'field' | 'sort' | 'dir' | 'page' | 'perPage'> = {}) =>
    api.get<FindingFieldsResponse>(
      `/normalization/catalogs/${catalogId}/finding-fields${findingsQueryString(params)}`,
    ),
  // Applies a finding's proposed change.
  //
  // Per-row Fix (`entityId` set, SC-2384): scopes to ONE entity's cell — strict
  // and synchronous, `204` applied / `409` CellDriftError on drift. Unchanged.
  //
  // Whole finding (`entityId` omitted, SC-2580): the apply moves off the request
  // thread — the BE returns `202` + an ApplyJobResponse and the caller polls
  // `applyProgress` until the job is terminal (lenient: drifted cells are skipped
  // server-side, no per-cell 409). Until the BE ships the 202 flip it still
  // returns `204` synchronously, so we feature-detect on the status code via
  // `postWithStatus`: `204` → `applied`, `202` → `queued`. A whole-finding apply
  // can still `409` *before* any `202` (already-applied / nothing-to-apply),
  // which propagates as an ApiError exactly like today's apply errors.
  apply: (catalogId: string, findingId: string, entityId?: string): Promise<ApplyResult> =>
    api
      .postWithStatus<ApplyJobResponse | ApplyOutcome | undefined>(
        `/normalization/catalogs/${catalogId}/findings/${findingId}/apply`,
        entityId ? { entity_id: entityId } : undefined,
      )
      .then(({ data, status }): ApplyResult => {
        if (status === 202 && data && 'job_id' in data) return { kind: 'queued', job: data };
        // The per-entity `200` carries the apply ledger inline (SC-2908) — keep it
        // so the card can show "k of N applied" without a poll round-trip. The
        // whole-finding sync `204` has no body.
        return {
          kind: 'applied',
          outcome: data && 'open_entity_ids' in data ? data : null,
        };
      }),
  // Polls an async whole-finding apply (SC-2580). `GET …/apply` projects the
  // job's live progress; `status: null` = no apply job has ever existed.
  applyProgress: (catalogId: string, findingId: string) =>
    api.get<ApplyProgress>(
      `/normalization/catalogs/${catalogId}/findings/${findingId}/apply`,
    ),
  // Acknowledges a finding (SC-2418) — "reviewed, no change". 204 No Content,
  // no body; the finding leaves the active queue but stays on record.
  acknowledge: (catalogId: string, findingId: string) =>
    api.post<void>(
      `/normalization/catalogs/${catalogId}/findings/${findingId}/acknowledge`,
    ),
  // Dismisses a finding (SC-2271). Mirror of apply — 204 No Content, no body
  // (a permanent dismiss; the snooze-only `until` field is unused here). The
  // finding drops out of the active buckets + counts server-side.
  dismiss: (catalogId: string, findingId: string) =>
    api.post<void>(
      `/normalization/catalogs/${catalogId}/findings/${findingId}/dismiss`,
    ),
};

export interface ListHistoryParams {
  action?: HistoryAction[];
  // ISO-8601 inclusive lower bound (SC-2768) — "what changed since …". Drives the
  // History view's agent `open_view` since filter.
  since?: string;
  page?: number;
  perPage?: number;
}

// SC-3424 History feed — the change ledger merged with acknowledge / dismiss
// judgments, newest first. Rows are self-contained historical records.
export const historyApi = {
  list: (catalogId: string, params: ListHistoryParams = {}) => {
    const qs = new URLSearchParams();
    params.action?.forEach((a) => qs.append('action', a));
    if (params.since) qs.set('since', params.since);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.perPage !== undefined) qs.set('per_page', String(params.perPage));
    const query = qs.toString();
    return api.get<PaginatedHistoryResponse>(
      `/normalization/catalogs/${catalogId}/history${query ? `?${query}` : ''}`,
    );
  },
};

// SC-2445 — per-catalog "Catalog Info" context (the structured onboarding
// context surfaced into LLM prompts). GET 404s until first saved, which the tab
// treats as a clean empty form (not an error); PATCH upserts. BE half: SC-2448.
export const catalogContextApi = {
  get: (catalogId: string) =>
    api.get<CatalogContext>(`/catalogs/${catalogId}/context`),
  update: (catalogId: string, body: CatalogContextInput) =>
    api.patch<CatalogContext>(`/catalogs/${catalogId}/context`, body),
};

export const profileApi = {
  get: (catalogId: string) =>
    api.get<CatalogProfileResponse>(`/normalization/catalogs/${catalogId}/profile`),
  // SC-2381 attributes view — the schema axis: per-field role/group/description +
  // metadata, plus the reconciled drawer sections. Same `/normalization` prefix
  // and 404-until-profiled surface as `get` (it's a projection of the same
  // profile doc). Powers the SC-2392 drawer field grouping.
  attributes: (catalogId: string) =>
    api.get<AttributesResponse>(`/normalization/catalogs/${catalogId}/attributes`),
};

// Drives the AnalysisStatusBar (global footer) + AnalysisInProgressBanner +
// "refreshing" treatment on the Catalog Profile and Findings Inbox surfaces.
// Polled while `state === 'running'` by useAnalysisStatus in api/hooks.ts.
export const analysisStatusApi = {
  get: (catalogId: string) =>
    api.get<AnalysisStatusResponse>(
      `/normalization/catalogs/${catalogId}/analysis-status`,
    ),
};

// Briefing endpoint (SC-2242 split from the old `/dashboard` composite).
// `?refresh=true` forces BE narrative regen subject to the pipeline gate;
// FE owns the session-start contract (default trigger: stale-by-30min
// computed against `briefing.narrative.generated_at`).
export const briefingApi = {
  get: (catalogId: string, { refresh = false }: { refresh?: boolean } = {}) =>
    api.get<BriefingResponse>(
      `/catalogs/${catalogId}/briefing${refresh ? '?refresh=true' : ''}`,
    ),
};

// Live bucket counts (SC-2242). Pure DB read, no caching on the BE side;
// FE coordinates suppression during pipeline runs via useAnalysisStatus
// (counts return 0/0/0 legitimately mid-pipeline — same shape as a truly
// empty catalog, so the FE distinguishes via the status hook).
export const findingCountsApi = {
  get: (catalogId: string) =>
    api.get<FindingCountsResponse>(
      `/normalization/catalogs/${catalogId}/finding-counts`,
    ),
};
